// planificationDraw.js — Phase 2B (Iter96) -> extended Phase 2C (Iter99):
// annotations (Point mode), per-feature custom styling (color/opacity),
// and direct store sync primitives for Undo/Redo (spec §6.6).
//
// Terra Draw integration for Planification: 4 Phase 2B tools (polygon,
// line, measure-distance, measure-area) + Phase 2C's annotation tool, on
// top of the Phase 2A engine. Never touches Carte/HeatmapCommandCenter,
// never imports alert/dispatch/agency code.
//
// MODE NAMING: Terra Draw lets one mode CLASS be registered multiple
// times under different `modeName`s — used here so "draw a line"
// (purpose=drawing) and "measure a distance" (purpose=measurement) are
// two independently-styled, independently-tracked Terra Draw modes even
// though both are backed by `TerraDrawLineStringMode`. Same for polygon
// vs measure-area. This is why `contractForMode()` below maps Terra
// Draw's mode name back to OUR `{objectType, purpose}` contract instead
// of re-deriving it from the geometry shape alone.
import { TerraDraw, TerraDrawSelectMode, TerraDrawPolygonMode, TerraDrawLineStringMode, TerraDrawPointMode } from "terra-draw";
import { TerraDrawMapLibreGLAdapter } from "terra-draw-maplibre-gl-adapter";
import { OBJECT_TYPES, PURPOSES, validatePolygonGeometryDetailed, validateLineGeometryDetailed } from "./planificationGeo";

export const MODE_NAMES = Object.freeze({
  POLYGON: "polygon",
  LINESTRING: "linestring",
  MEASURE_POLYGON: "measure-polygon",
  MEASURE_LINE: "measure-line",
  ANNOTATION: "annotation",
});

/** Toolbar tool key -> Terra Draw mode name. "pan" maps to Terra Draw's
 *  built-in "select" mode (not the fully-inert "static" mode): this is
 *  what lets a user, while the map is otherwise in its default/idle
 *  state, click an EXISTING polygon/line/annotation to select + edit it
 *  (spec Phase 2C §5), while a drag that does NOT start on a draggable
 *  feature/vertex still falls straight through to MapLibre's native
 *  drag-pan — Select mode only ever calls the adapter's
 *  `setDraggability(false)` for the duration of an actual vertex/feature
 *  drag it is handling, never for an idle empty-space drag (spec §9:
 *  "un glissement déplace la carte" / "un toucher ne crée aucun
 *  sommet"). */
export const TOOL_TO_MODE = Object.freeze({
  pan: "select",
  draw_polygon: MODE_NAMES.POLYGON,
  draw_line: MODE_NAMES.LINESTRING,
  measure_area: MODE_NAMES.MEASURE_POLYGON,
  measure_distance: MODE_NAMES.MEASURE_LINE,
  annotation: MODE_NAMES.ANNOTATION,
});

const MODE_CONTRACT = Object.freeze({
  [MODE_NAMES.POLYGON]: { objectType: OBJECT_TYPES.POLYGON, purpose: PURPOSES.DRAWING },
  [MODE_NAMES.LINESTRING]: { objectType: OBJECT_TYPES.LINE, purpose: PURPOSES.DRAWING },
  [MODE_NAMES.MEASURE_POLYGON]: { objectType: OBJECT_TYPES.POLYGON, purpose: PURPOSES.MEASUREMENT },
  [MODE_NAMES.MEASURE_LINE]: { objectType: OBJECT_TYPES.LINE, purpose: PURPOSES.MEASUREMENT },
  [MODE_NAMES.ANNOTATION]: { objectType: OBJECT_TYPES.ANNOTATION, purpose: PURPOSES.DRAWING },
});

/** Default per-object-type fallback color/opacity — used by the
 *  function-based Terra Draw styles below BEFORE a feature has an
 *  explicit `properties.color`/`properties.opacity` of its own (i.e. the
 *  brief window between a draw-mode's internal feature creation and our
 *  `onFinish` handler syncing the session's chosen default color back in
 *  via `draw.updateFeatureProperties`). Kept identical to Phase 2B's
 *  previous static per-mode colors so there is no visual regression. */
const DEFAULT_STYLE_BY_MODE = Object.freeze({
  [MODE_NAMES.POLYGON]: { color: "#00B894", opacity: 0.4 },
  [MODE_NAMES.LINESTRING]: { color: "#00B894", opacity: 0.4 },
  [MODE_NAMES.MEASURE_POLYGON]: { color: "#f59e0b", opacity: 0.25 },
  [MODE_NAMES.MEASURE_LINE]: { color: "#f59e0b", opacity: 0.25 },
  [MODE_NAMES.ANNOTATION]: { color: "#3b82f6", opacity: 1 },
});

const colorOf = (modeName) => (feature) => feature.properties.color || DEFAULT_STYLE_BY_MODE[modeName].color;
const opacityOf = (modeName) => (feature) => {
  const v = feature.properties.opacity;
  return typeof v === "number" ? v : DEFAULT_STYLE_BY_MODE[modeName].opacity;
};

/** Terra Draw mode name -> our GeoJSON contract {objectType, purpose}. */
export const contractForMode = (modeName) => MODE_CONTRACT[modeName] || null;

/** Reverse of the above — our GeoJSON contract {objectType, purpose} ->
 *  Terra Draw mode name. Used to rehydrate `planificationSession.js`'s
 *  FeatureCollection (the only contract it knows) back into a freshly
 *  (re)created Terra Draw instance after a basemap style switch. */
export const modeNameForContract = (objectType, purpose) => {
  const entry = Object.entries(MODE_CONTRACT).find(([, c]) => c.objectType === objectType && c.purpose === purpose);
  return entry ? entry[0] : null;
};

/**
 * Wraps our detailed validators into the `Validation` shape Terra Draw
 * expects. Terra Draw calls this hook on EVERY intermediate "commit" as
 * each vertex is added mid-draw — NOT just once at the very end — so a
 * polygon with only 2 vertices placed so far is not yet expected to
 * satisfy our strict "closed ring, >=3 distinct vertices, non-self-
 * intersecting" rules. Confirmed via a direct Node reproduction against
 * the real `terra-draw` package (bypassing MapLibre/React entirely):
 * rejecting an earlier "commit" causes Terra Draw to roll back that
 * vertex addition — symptom observed in QA as the vertex counter being
 * permanently stuck at 1 and spurious "duplicate vertex" errors on
 * finish. Only `updateType === "finish"` represents the user actually
 * trying to complete the shape — that is the ONLY point our strict
 * validator should run.
 */
const makeValidation = (objectType, onInvalidFinish) => (feature, context) => {
  if (context.updateType !== "finish") return { valid: true };
  const detail = objectType === OBJECT_TYPES.POLYGON
    ? validatePolygonGeometryDetailed(feature.geometry)
    : validateLineGeometryDetailed(feature.geometry);
  if (!detail.valid) {
    onInvalidFinish?.(detail.reason, objectType);
    return { valid: false, reason: detail.reason };
  }
  return { valid: true };
};

const buildModes = (onInvalidFinish) => {
  const polygonValidation = makeValidation(OBJECT_TYPES.POLYGON, onInvalidFinish);
  const lineValidation = makeValidation(OBJECT_TYPES.LINE, onInvalidFinish);
  const editableCoordinateFlags = { midpoints: { draggable: true }, draggable: true, deletable: true };

  return [
    // One shared Select mode handles edit (Phase 2B §4.4/§5.2, Phase 2C
    // §5) for all 5 modes — `flags` keyed by mode name is how Terra Draw
    // scopes which edit capabilities + which validation apply to a
    // selected feature, per the mode it was created under. Re-validated
    // on every edit commit, same rules as initial creation (spec: "sans
    // rendre la géométrie invalide"). Annotation is draggable but has no
    // geometry validator — a single Point position is always valid once
    // it passes the base RFC 7946 coordinate check Terra Draw itself runs.
    new TerraDrawSelectMode({
      flags: {
        [MODE_NAMES.POLYGON]: { feature: { draggable: true, validation: polygonValidation, coordinates: editableCoordinateFlags } },
        [MODE_NAMES.MEASURE_POLYGON]: { feature: { draggable: true, validation: polygonValidation, coordinates: editableCoordinateFlags } },
        [MODE_NAMES.LINESTRING]: { feature: { draggable: true, validation: lineValidation, coordinates: editableCoordinateFlags } },
        [MODE_NAMES.MEASURE_LINE]: { feature: { draggable: true, validation: lineValidation, coordinates: editableCoordinateFlags } },
        [MODE_NAMES.ANNOTATION]: { feature: { draggable: true } },
      },
    }),
    // Phase 2C (spec §5.3 "Modifier sa couleur"/"Modifier son
    // opacité"): every mode's fill/line/point color+opacity below is a
    // FUNCTION of the feature's OWN `properties.color`/`properties.opacity`
    // (Terra Draw calls style functions per-feature, per its documented
    // styling API) rather than one static color shared by every object of
    // that mode — this is what makes a per-object color picker actually
    // visible on the map instead of a silently-ignored cosmetic no-op.
    // Falls back to the Phase 2B defaults (`DEFAULT_STYLE_BY_MODE`) until
    // `onFinish`/`updateFeatureProperties` syncs an explicit value in.
    new TerraDrawPolygonMode({
      modeName: MODE_NAMES.POLYGON,
      editable: true,
      validation: polygonValidation,
      styles: { fillColor: colorOf(MODE_NAMES.POLYGON), outlineColor: colorOf(MODE_NAMES.POLYGON), fillOpacity: opacityOf(MODE_NAMES.POLYGON) },
    }),
    new TerraDrawLineStringMode({
      modeName: MODE_NAMES.LINESTRING,
      editable: true,
      validation: lineValidation,
      styles: { lineStringColor: colorOf(MODE_NAMES.LINESTRING) },
    }),
    // Measurement variants: same mode classes, distinct name + amber
    // default so they are visually distinguishable from a "real" drawn
    // object even though both ultimately produce a LineString/Polygon
    // (spec §6: "Cet outil est distinct du dessin d'une ligne ordinaire").
    new TerraDrawPolygonMode({
      modeName: MODE_NAMES.MEASURE_POLYGON,
      editable: true,
      validation: polygonValidation,
      styles: { fillColor: colorOf(MODE_NAMES.MEASURE_POLYGON), outlineColor: colorOf(MODE_NAMES.MEASURE_POLYGON), fillOpacity: opacityOf(MODE_NAMES.MEASURE_POLYGON) },
    }),
    new TerraDrawLineStringMode({
      modeName: MODE_NAMES.MEASURE_LINE,
      editable: true,
      validation: lineValidation,
      styles: { lineStringColor: colorOf(MODE_NAMES.MEASURE_LINE) },
    }),
    // Phase 2C — annotation marker. A Point feature finalizes on a
    // SINGLE click (no multi-vertex drawing phase, no `validation`
    // needed beyond Terra Draw's own base coordinate check) — the
    // controller below intercepts its `finish` event to defer session
    // creation until the title/description form is actually saved
    // (spec §4.1: "une annotation non validée ne doit pas être ajoutée
    // à l'historique").
    new TerraDrawPointMode({
      modeName: MODE_NAMES.ANNOTATION,
      styles: {
        pointColor: colorOf(MODE_NAMES.ANNOTATION),
        pointOutlineColor: "#ffffff",
        pointOutlineWidth: 2,
        pointWidth: 7,
        pointOpacity: opacityOf(MODE_NAMES.ANNOTATION),
      },
    }),
  ];
};

/** Dispatches a REAL synthetic keyup on the map's canvas — the only
 *  public mechanism Terra Draw exposes for finishing/cancelling a draw
 *  (its `close()`/cancel handling is private; both are wired to listen
 *  for keyup on `adapter.getMapEventElement()`, which for the MapLibre
 *  adapter is `map.getCanvas()`). This is what both our own mobile
 *  "Close polygon"/"Finish line" floating buttons AND the (bonus, not
 *  spec-required) desktop Enter/Escape keys rely on. */
const dispatchKey = (map, key) => {
  map.getCanvas().dispatchEvent(new KeyboardEvent("keyup", { key, bubbles: true, cancelable: true }));
};

/**
 * Reads the vertex count of whatever feature is currently being drawn
 * in `modeName` (or 0 if nothing is in progress) — used to gate the
 * mobile "Close polygon"/"Finish line" floating buttons' visibility
 * (spec §4.3/§5.1: button appears once the minimum vertex count is
 * reached). Uses Terra Draw's own `committedCoordinateCount` (clicks the
 * user actually made) rather than raw `geometry.coordinates.length`,
 * which also includes the live mouse-follow/rubber-band point.
 */
export const getCurrentlyDrawingVertexCount = (draw, modeName) => {
  const feature = draw.getSnapshot().find((f) => f.properties.mode === modeName && f.properties.currentlyDrawing);
  if (!feature) return 0;
  const committed = feature.properties.committedCoordinateCount;
  if (typeof committed === "number") return committed;
  // Defensive fallback only — geometry-shape-aware (Polygon coordinates
  // are `[ring]`, so the vertex array is `coordinates[0]`, NOT
  // `coordinates` itself; using the wrong one silently returns 0 for
  // every polygon regardless of how many vertices are placed).
  const coords = feature.geometry?.type === "Polygon" ? feature.geometry.coordinates?.[0] : feature.geometry?.coordinates;
  return Math.max(0, (coords?.length || 0) - 1);
};

/** Same lookup, but returns the full in-progress feature (or null) — used
 *  for the live segment/total-distance and area readout while drawing. */
export const getCurrentlyDrawingFeature = (draw, modeName) =>
  draw.getSnapshot().find((f) => f.properties.mode === modeName && f.properties.currentlyDrawing) || null;

/**
 * Maps OUR session Feature (contract: properties.object_type/purpose/
 * name/description/color/opacity/created_at) to a Terra-Draw-loadable
 * feature (contract: properties.mode, + color/opacity passthrough for
 * the Phase 2C per-feature style functions above) for re-injection via
 * `draw.addFeatures()` after a style switch OR an Undo/Redo restore. The
 * SAME `id` is always reused — Terra Draw's default idStrategy is
 * UUID4, so our session Feature's `id` (originally generated BY Terra
 * Draw on creation, see `planificationSession.js`'s finish-event
 * handler) round-trips exactly, satisfying "aucun UUID régénéré"
 * (spec §9/§10).
 */
export const sessionFeatureToTerraDrawFeature = (feature, modeName) => ({
  id: feature.id,
  type: "Feature",
  geometry: feature.geometry,
  properties: { mode: modeName, color: feature.properties.color, opacity: feature.properties.opacity },
});

/**
 * Attaches a Terra Draw controller to `map`. One instance per map
 * lifetime slice (re-created on every basemap style switch by the
 * caller, see PlanificationTab.jsx) — never double-started.
 *
 * @param {import('maplibre-gl').Map} map
 * @param {{
 *   initialFeatures?: {feature: object, modeName: string}[], // rehydration after a style switch
 *   onInvalidFinish?: (reasonCode: string, objectType: string) => void,
 *   onFeatureFinalized?: (id: string, modeName: string, geometry: object, isNew: boolean) => void,
 *   onFeatureDeleted?: (id: string) => void,
 *   onAnnotationDrafted?: (id: string, geometry: object) => void, // Phase 2C — point placed, awaiting the title/description form
 *   onSelect?: (id: string) => void,   // Phase 2C §5.1
 *   onDeselect?: (id: string) => void, // Phase 2C §5.1
 *   onChange?: (ids: string[], type: string) => void, // raw passthrough, for live-measurement UI polling
 * }} handlers
 */
export const createPlanificationDrawController = (map, handlers = {}) => {
  if (!map) throw new Error("createPlanificationDrawController: map instance required");
  const {
    initialFeatures = [], onInvalidFinish, onFeatureFinalized, onFeatureDeleted,
    onAnnotationDrafted, onSelect, onDeselect, onChange: onChangeExternal,
  } = handlers;

  const adapter = new TerraDrawMapLibreGLAdapter({ map });
  const draw = new TerraDraw({ adapter, modes: buildModes(onInvalidFinish) });

  // Phase 2C (spec §4.1): a Point feature finalizes on a SINGLE click —
  // Terra Draw's own `finish` fires immediately, before any title has
  // been entered. Routing it to `onAnnotationDrafted` instead of
  // `onFeatureFinalized` means it is NEVER added to the session
  // FeatureCollection (so never historised) until the caller's form is
  // actually saved — cancelling the form simply calls
  // `draw.removeFeatures([id])`, leaving no trace anywhere.
  const onFinish = (id, context) => {
    const snapshotFeature = draw.getSnapshotFeature(id);
    if (!snapshotFeature) return; // deleted/edit-cancelled between finish firing and this read — nothing to sync
    if (snapshotFeature.properties.mode === MODE_NAMES.ANNOTATION && context.action === "draw") {
      onAnnotationDrafted?.(id, snapshotFeature.geometry);
      return;
    }
    onFeatureFinalized?.(id, snapshotFeature.properties.mode, snapshotFeature.geometry, context.action === "draw");
  };
  const onChange = (ids, type) => {
    if (type === "delete") ids.forEach((id) => onFeatureDeleted?.(id));
    onChangeExternal?.(ids, type);
  };
  const handleSelect = (id) => onSelect?.(id);
  const handleDeselect = (id) => onDeselect?.(id);
  draw.on("finish", onFinish);
  draw.on("change", onChange);
  draw.on("select", handleSelect);
  draw.on("deselect", handleDeselect);

  let started = false;
  const start = () => {
    if (started) return; // double-init guard
    draw.start();
    started = true;
    if (initialFeatures.length) {
      try {
        draw.addFeatures(initialFeatures.map(({ feature, modeName }) => sessionFeatureToTerraDrawFeature(feature, modeName)));
      } catch (e) {
        // One malformed persisted feature should never prevent every other
        // already-finalized shape from re-rendering after a style switch —
        // fall back to re-adding one at a time, skipping only the bad one.
        initialFeatures.forEach(({ feature, modeName }) => {
          try { draw.addFeatures([sessionFeatureToTerraDrawFeature(feature, modeName)]); }
          catch (_e2) { console.warn("[planificationDraw] skipped rehydrating feature", feature.id, _e2); }
        });
      }
    }
  };

  const destroy = () => {
    if (!started) return;
    draw.off("finish", onFinish);
    draw.off("change", onChange);
    draw.off("select", handleSelect);
    draw.off("deselect", handleDeselect);
    try { draw.stop(); } catch (_e) { /* map already torn down — safe to ignore */ }
    started = false;
  };

  const setActiveTool = (toolKey) => {
    const modeName = TOOL_TO_MODE[toolKey];
    if (modeName) draw.setMode(modeName);
  };

  const finishCurrentDrawing = () => dispatchKey(map, "Enter");
  const cancelCurrentDrawing = () => dispatchKey(map, "Escape");

  start();
  return { draw, destroy, setActiveTool, finishCurrentDrawing, cancelCurrentDrawing };
};
